import { yupResolver } from '@hookform/resolvers/yup'
import { find, map } from 'lodash-es'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Button, Card, Input } from '@/components/ui'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import { Days } from '../../../../common/models'
import {
  IAlcatrazInterval,
  IAlcatrazSettings,
} from '../../../../common/models/settings'
import FormGroup from '../../../../core/components/FormGroup'
import { formatDateTime } from '../../../../core/constants/times'
import { getFestivalDays } from '../../../../core/services/dates-settings'
import {
  getAlcatrazSettings,
  updateAlcatrazSettings,
} from '../../../../core/services/festival-settings'
import { useAppSelector } from '../../../../store'
import { MessageBox } from '../../messages/MessageBox'

interface IAlcatrazValues {
  apiKey?: string
  cardFormatId: string
  accountId: string
}

interface IRegistrationWindow {
  startAt?: number
  endAt?: number
}

const buildRegistrationWindows = (intervals?: IAlcatrazInterval[]) => {
  const next: Record<string, IRegistrationWindow> = {}
  map(intervals, (interval) => {
    next[interval.festivalDayId] = {
      startAt: interval.startAt,
      endAt: interval.endAt,
    }
  })
  return next
}

export const Alcatraz: React.FC<{ hasEditPermission: boolean }> = ({
  hasEditPermission,
}) => {
  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const activeEditionId = storeFestivals
    .find((f) => f.platformName === festivalName)
    ?.editions.find((e) => e.active)?.id

  const [alcatrazSettings, setAlcatrazSettings] = useState<IAlcatrazSettings>()
  const [days, setDays] = useState<Days[]>([])
  const [registrationWindows, setRegistrationWindows] = useState<
    Record<string, IRegistrationWindow>
  >({})
  const [registrationWindowErrors, setRegistrationWindowErrors] = useState<
    Record<string, string>
  >({})
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getAlcatrazSettings()
        if (resp.status === 200) {
          setAlcatrazSettings(resp.data)
          setValue('cardFormatId', resp.data.cardFormatId)
          setValue('accountId', resp.data.accountId)
          setRegistrationWindows(buildRegistrationWindows(resp.data.intervals))
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        if (activeEditionId) {
          const resp = await getFestivalDays(activeEditionId)
          setDays(resp.data)
        } else {
          setDays([])
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      }
    })()
  }, [activeEditionId])

  const schema = yup.object().shape({
    apiKey: yup.string().optional(),
    cardFormatId: yup.string().required(),
    accountId: yup.string().required(),
  })

  const {
    handleSubmit,
    setValue,
    register,
    formState: { errors },
  } = useForm<IAlcatrazValues>({
    mode: 'onChange',
    resolver: yupResolver<IAlcatrazValues>(schema),
    defaultValues: {
      apiKey: '',
      cardFormatId: '',
      accountId: '',
    },
  })

  const setRegistrationWindowValue = (
    festivalDayId: string,
    field: 'startAt' | 'endAt',
    date: Date | null,
  ) => {
    setRegistrationWindows((prev) => ({
      ...prev,
      [festivalDayId]: {
        ...prev[festivalDayId],
        [field]: date ? date.valueOf() : undefined,
      },
    }))
    setRegistrationWindowErrors((prev) => ({
      ...prev,
      [festivalDayId]: '',
    }))
  }

  const submit = async (values: IAlcatrazValues) => {
    const nextErrors: Record<string, string> = {}
    const intervals: IAlcatrazInterval[] = []
    map(days, (d) => {
      const window = registrationWindows[d.id]
      if (window?.startAt && window?.endAt) {
        if (window.startAt >= window.endAt) {
          nextErrors[d.id] = 'Window start must be before window end'
        } else {
          intervals.push({
            festivalDayId: d.id,
            startAt: window.startAt,
            endAt: window.endAt,
          })
        }
      }
    })
    setRegistrationWindowErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) {
      return
    }

    try {
      const { data, status } = await updateAlcatrazSettings({
        ...values,
        intervals,
      })
      if (status === 200) {
        setAlcatrazSettings(data)
        setValue('apiKey', '')
        setRegistrationWindows(buildRegistrationWindows(data.intervals))
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    if (alcatrazSettings) {
      setValue('cardFormatId', alcatrazSettings.cardFormatId)
      setValue('accountId', alcatrazSettings.accountId)
      setRegistrationWindows(
        buildRegistrationWindows(alcatrazSettings.intervals),
      )
    }
    setValue('apiKey', '')
    setRegistrationWindowErrors({})
  }

  return (
    <>
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
        <form
          id="settings-alcatraz-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-[28px]"
          autoComplete="off"
        >
          <h3 className="text-title-xs text-monochrome-1 mb-4">Alcatraz</h3>
          <FormGroup label="API Key" error={errors?.apiKey?.message}>
            <Input
              isInvalid={!!errors?.apiKey?.message}
              {...register('apiKey')}
              type="password"
              placeholder="Leave blank to keep current"
              autoComplete="new-password"
              disabled={!hasEditPermission}
            />
          </FormGroup>
          <FormGroup
            label="Card Format ID"
            error={errors?.cardFormatId?.message}
          >
            <Input
              isInvalid={!!errors?.cardFormatId?.message}
              {...register('cardFormatId')}
              type="text"
              placeholder="Enter Card Format ID"
              autoComplete="off"
              disabled={!hasEditPermission}
            />
          </FormGroup>
          <FormGroup label="Account ID" error={errors?.accountId?.message}>
            <Input
              isInvalid={!!errors?.accountId?.message}
              {...register('accountId')}
              type="text"
              placeholder="Enter Account ID"
              autoComplete="off"
              disabled={!hasEditPermission}
            />
          </FormGroup>

          <div>
            <h4 className="text-title-xs text-monochrome-1 mb-2">
              Registration windows
            </h4>
            {!activeEditionId && (
              <p className="text-body-medium text-monochrome-1/70">
                No active festival edition — registration windows unavailable.
              </p>
            )}
            {!!activeEditionId && days.length === 0 && (
              <p className="text-body-medium text-monochrome-1/70">
                No festival days found for this edition.
              </p>
            )}
            {map(days, (d, index) => {
              const name =
                find(d.translations, (t) => t.locale === 'en')?.name ||
                `Day ${index + 1}`
              const window = registrationWindows[d.id]
              return (
                <Card
                  key={d.id}
                  className="p-4 bg-monochrome-3-60 border border-monochrome-120 mt-2"
                >
                  <div className="flex justify-between gap-2 mb-2">
                    <h6 className="text-title-xs text-monochrome-1">{name}</h6>
                    <span className="text-body-medium text-monochrome-1/70">
                      {formatDateTime(d.startAt)} - {formatDateTime(d.endAt)}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <FormGroup
                      label="Window Start"
                      error={registrationWindowErrors[d.id]}
                    >
                      <DateTimePicker
                        className="h-[44px] rounded-2xl"
                        value={
                          window?.startAt ? new Date(window.startAt) : null
                        }
                        onChange={(date) =>
                          setRegistrationWindowValue(d.id, 'startAt', date)
                        }
                        placeholder="Enter Window Start"
                        hasError={!!registrationWindowErrors[d.id]}
                        disabled={!hasEditPermission}
                        maxDate={
                          window?.endAt ? new Date(window.endAt) : undefined
                        }
                      />
                    </FormGroup>
                    <FormGroup label="Window End">
                      <DateTimePicker
                        className="h-[44px] rounded-2xl"
                        value={window?.endAt ? new Date(window.endAt) : null}
                        onChange={(date) =>
                          setRegistrationWindowValue(d.id, 'endAt', date)
                        }
                        placeholder="Enter Window End"
                        hasError={!!registrationWindowErrors[d.id]}
                        disabled={!hasEditPermission}
                        minDate={
                          window?.startAt ? new Date(window.startAt) : undefined
                        }
                      />
                    </FormGroup>
                  </div>
                </Card>
              )
            })}
          </div>
        </form>

        <div className="flex justify-end gap-2 border-t border-monochrome-120 mt-4 [&>*]:w-[154px] pt-4">
          <Button
            onClick={handleCancel}
            variant="secondary"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            form="settings-alcatraz-form"
            type="submit"
            variant="primary"
            disabled={!hasEditPermission}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
